import React, { useState } from 'react';
import Sidebar from './Sidebar';
import FeedbackWidget from '../FeedbackWidget';
import TrialCountdownBanner from '../TrialCountdownBanner';
import BottomTabBar from '../native/BottomTabBar';
import { isNative } from '../../utils/capacitor';
import { useTheme } from '../../context/ThemeContext';

// Accepts optional controlled props: sidebarOpen + onSidebarChange
// When provided, the parent owns the open/close state (used by ProductTour).
// When omitted, DashboardLayout manages its own state as before.
const DashboardLayout = ({ children, title, sidebarOpen: controlledOpen, onSidebarChange, tourActive }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const { darkMode } = useTheme();
  const native = isNative();

  const isControlled  = controlledOpen !== undefined;
  const sidebarOpen   = isControlled ? controlledOpen : internalOpen;
  const setSidebarOpen = (val) => {
    if (isControlled) onSidebarChange?.(val);
    else setInternalOpen(val);
  };

  return (
    <div className="d-flex" style={{
      // On native #root is already inset by the safe areas, so fill exactly the
      // remaining height (avoids the layout overflowing past the home indicator).
      minHeight: native ? 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))' : '100vh',
      background:'var(--page-bg)',
    }}>
      {/* Desktop Sidebar — hidden on native (the bottom tab bar is primary nav) */}
      {!native && (
        <div className="d-none d-lg-flex" style={{position:'sticky', top:0, height:'100vh', flexShrink:0, alignSelf:'flex-start'}}>
          <Sidebar />
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        // When the tour is active: no dim backdrop (tour provides it), sidebar sits above tour backdrop (z 10001) but below tooltip (z 10002)
        <div
          className={`${native ? '' : 'd-lg-none'} position-fixed top-0 start-0 w-100 h-100`}
          style={{
            zIndex: tourActive ? 10001 : 1050,
            background: tourActive ? 'transparent' : 'rgba(0,0,0,0.5)',
          }}
          onClick={() => { if (!tourActive) setSidebarOpen(false); }}
        >
          <div onClick={e => e.stopPropagation()}>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-grow-1 d-flex flex-column overflow-hidden">
        {/* Top bar — mobile on web, always on native (with notch-safe padding) */}
        <div className={`${native ? '' : 'd-lg-none'} d-flex align-items-center justify-content-between px-3 py-2 border-bottom`}
          style={{
            background:'var(--card-bg)',
            borderBottomColor: darkMode ? '#2d1b69' : undefined,
            boxShadow: darkMode ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
            // The notch inset is handled globally on #root, so the bar just sits
            // at the top of the (already inset) app — sticky within the scroll col.
            position: native ? 'sticky' : undefined,
            top: native ? 0 : undefined,
            zIndex: native ? 1020 : undefined,
          }}>
          <button className="btn btn-link p-0" onClick={() => setSidebarOpen(true)}>
            <i className="bi bi-list fs-4" style={{color:'#6f42c1'}}></i>
          </button>
          <span style={{fontFamily:"'Fredoka One',cursive", color:'#6f42c1', fontSize:'20px'}}>🌙 Dreamy Bedtime</span>
          <div style={{width:'32px'}}></div>
        </div>

        <main className="flex-grow-1 overflow-auto p-3 p-lg-4" style={native ? { paddingBottom: 76 } : undefined}>
          {/* Trial countdown — only renders in last 5 days of trial */}
          <TrialCountdownBanner />
          {title && (
            <div className="mb-4">
              <h2 className="fw-bold mb-0" style={{fontFamily:"'Fredoka One',cursive", color:'var(--text-main)'}}>{title}</h2>
            </div>
          )}
          {children}
        </main>
      </div>
      {native ? <BottomTabBar /> : <FeedbackWidget />}
    </div>
  );
};

export default DashboardLayout;
